/**
 * Window render target — opens a same-origin popup and boots the Pype runtime
 * inside it, so the widget renders in its own browser window instead of the
 * host page.
 *
 * Design (see `CLAUDE.md`): the popup is opened blank (`about:blank`, which
 * inherits the opener's origin, so it shares `localStorage` for session
 * continuity). We write a tiny bootstrap document that imports the Pype
 * runtime bundle IN THE POPUP'S OWN REALM — so `ps-pype` is defined there and
 * XState/WebSocket run inside the popup. The Loader then holds a LIVE reference
 * to the popup's `<ps-pype>` element and drives it directly (same-origin
 * cross-window calls), which is why every Loader method/getter/event behaves
 * exactly as it does inline. There is NO serialized command bridge — that
 * couldn't keep the synchronous getters (`isOpen`, `can`, `getLauncherState`)
 * synchronous.
 *
 * This module owns ONLY the popup mechanics (open, bootstrap, handshake,
 * lifecycle watching). The element creation, config/session wiring, and event
 * fan-out stay in `loader.ts`, reused verbatim from the inline path.
 *
 * @module
 */

import { LoaderErrorCode, PypeLoaderError, type AppConfig } from './types';

/** postMessage `source` the popup bootstrap sends once the runtime imported. */
const READY_MESSAGE = 'pype-window-ready';
/** postMessage `source` the popup bootstrap sends if the runtime import threw. */
const ERROR_MESSAGE = 'pype-window-error';
/** How often to poll `popup.closed` (ms) — the robust cross-browser close signal. */
const CLOSE_POLL_MS = 500;
/**
 * Last-resort deadline (ms) for the re-injected runtime handshake after a popup
 * reload. The bootstrap posts `pype-window-ready`/`pype-window-error`, so a
 * successful or failed re-import settles quickly; this only fires if the
 * re-`import()` STALLS (a hung fetch that never resolves or rejects), so the
 * popup can't be left blank and signal-less. Generous — the runtime bundle was
 * just loaded pre-refresh and is normally warm — so a slow-but-real re-import
 * isn't killed prematurely.
 */
const REINJECT_TIMEOUT_MS = 15_000;
/**
 * Default popup size by modality. Floating renders a compact chat window;
 * fullpage is a page layout and wants room to breathe. Both are overridable
 * via `config.window.width` / `config.window.height`.
 */
const DEFAULT_SIZE = {
  floating: { width: 420, height: 640 },
  fullpage: { width: 960, height: 720 },
} as const;

/**
 * Handle to an open popup render target. Returned by {@link openPypeWindow};
 * consumed by the Loader.
 */
export interface OpenWindowResult {
  /** The popup `Window` (same-origin). */
  readonly window: Window;
  /**
   * Resolves once the popup has imported the runtime and defined `ps-pype`
   * (the first `pype-window-ready` handshake). Rejects if the runtime import
   * failed inside the popup. Resolves immediately when {@link adopted}.
   */
  readonly ready: Promise<void>;
  /**
   * `true` when this call ADOPTED an already-running popup of the same window
   * name (a chat was live in it) instead of opening/writing a fresh one. Lets a
   * fresh Loader (host reloaded, or re-created per click) re-acquire the running
   * window and focus it rather than clobbering it into a new chat. The Loader
   * re-wires the existing `<ps-pype>` WITHOUT re-initializing it.
   */
  readonly adopted: boolean;
  /** Register a callback fired once when the popup is closed by the user. */
  onClose(cb: () => void): void;
  /**
   * Register a callback fired when the popup RELOADS (user refresh). Detected by
   * the close-poll noticing the runtime vanished from the popup's realm (a
   * `document.write`'d `about:blank` reloads to a blank page — the bootstrap
   * script does NOT re-run), so the opener's `<ps-pype>` reference is stale. The
   * handler decides whether to restore (via {@link reinject}) or tear down.
   * Only armed after {@link notifyMounted}.
   */
  onReload(cb: () => void): void;
  /**
   * Tell the handle a `<ps-pype>` is now mounted, arming reload detection. Must
   * be called after the initial mount AND after each successful remount — reload
   * detection is a no-op until armed, so a slow initial `import()` (runtime not
   * yet defined) can't be mistaken for a reload.
   */
  notifyMounted(): void;
  /**
   * Re-write the bootstrap document into the popup after a reload, reusing the
   * ORIGINAL nonce/runtime URL/origin. The re-injected script re-imports the
   * runtime in the popup's fresh realm; the caller then re-acquires `<ps-pype>`.
   *
   * Returns a promise that settles on the RE-INJECTED runtime's handshake:
   * resolves on `pype-window-ready`, rejects on `pype-window-error`, on popup
   * close mid-reinject, or after {@link REINJECT_TIMEOUT_MS} if neither arrives
   * (a stalled re-fetch that never resolves or rejects). This is the reload-path
   * analogue of {@link ready}: awaiting it lets the caller surface a failed
   * re-import instead of hanging on `whenDefined('ps-pype')` forever.
   */
  reinject(): Promise<void>;
  /** Bring the popup to the foreground. */
  focus(): void;
  /** Intentionally close the popup and stop lifecycle watchers (no `onClose`). */
  close(): void;
  /** Whether the popup has been closed (intentionally or by the user). */
  readonly closed: boolean;
}

/**
 * Resolve the absolute URL the popup should `import()` to load the Pype
 * runtime. On the CDN build this comes from the global the loader entry sets
 * (`__PYPE_RUNTIME_URL__`, same origin as `loader.js`); bundler/npm consumers
 * must pass `window.bootstrapUrl` since there is no such global.
 *
 * @throws {PypeLoaderError} `WINDOW_BLOCKED` when no URL can be resolved.
 */
export function resolveWindowRuntimeUrl(config: AppConfig): string {
  const fromGlobal = (globalThis as { __PYPE_RUNTIME_URL__?: string })
    .__PYPE_RUNTIME_URL__;
  const url = config.window?.bootstrapUrl ?? fromGlobal;
  if (!url) {
    throw new PypeLoaderError(
      LoaderErrorCode.WINDOW_BLOCKED,
      "target: 'window' could not resolve the Pype runtime URL. On the CDN " +
        'build it is auto-resolved; for bundler/npm consumers pass ' +
        "`window: { bootstrapUrl: '<url-to-@pypestream/pype-runtime>' }`."
    );
  }
  return url;
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch {
    /* callback/DOM errors must never escape into the host stack */
  }
}

/**
 * Whether a same-name popup already has a LIVE Pype runtime — its custom-element
 * registry has `ps-pype` AND an instance is in the DOM. When true the popup is
 * adopted (focus, don't clobber) instead of starting a new chat, so a fresh
 * Loader (host reload / re-created per click) reuses the running window. Guarded
 * — cross-context access can throw.
 */
function runtimeAlreadyRunning(popup: Window): boolean {
  try {
    return (
      !!popup.customElements?.get('ps-pype') &&
      !!popup.document.querySelector('ps-pype')
    );
  } catch {
    return false;
  }
}

/**
 * Expando set on the popup's own `window` while its bootstrap runtime import is
 * IN FLIGHT — after the `document.write` but before `ps-pype` is defined. Lets a
 * rapid SECOND `openPypeWindow` on the same-name window (a fresh per-click Loader
 * + double-click) distinguish "mid-bootstrap" from "genuinely blank" and adopt
 * instead of clobbering the in-flight chat with another `document.write`. Cleared
 * once the handshake settles (below) and naturally on reload (realm reset).
 */
const BOOTSTRAPPING_FLAG = '__PYPE_BOOTSTRAPPING__';

function setBootstrapping(popup: Window, value: boolean): void {
  try {
    (popup as unknown as Record<string, unknown>)[BOOTSTRAPPING_FLAG] = value;
  } catch {
    /* cross-context property access can throw mid-teardown — best-effort */
  }
}

/** Whether a same-name popup is mid-bootstrap (import in flight, `ps-pype` not yet defined). */
function runtimeBootstrapping(popup: Window): boolean {
  try {
    return !!(popup as unknown as Record<string, unknown>)[BOOTSTRAPPING_FLAG];
  } catch {
    return false;
  }
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"]/g,
    (c) =>
      (
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }) as Record<
          string,
          string
        >
      )[c]
  );
}

/**
 * JSON-encode a value for safe embedding inside an inline `<script>`. Plain
 * `JSON.stringify` does NOT neutralize a `</script>` sequence, so a value
 * containing one would break out of the script element; replacing `<` with its
 * unicode escape (equivalent inside a JS string literal) closes that.
 */
function jsString(value: string): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

/**
 * Compute the `window.open` feature string. A caller-supplied `features`
 * string wins outright; otherwise just `width`x`height` — the browser places
 * the popup.
 *
 * No `left`/`top` position and no `popup=yes`: width/height alone already yields
 * a popup window, and letting the browser place it matches legacy
 * (`consumer-apps` api-launcher never positioned it).
 */
function buildFeatures(config: AppConfig): string {
  if (config.window?.features) return config.window.features;

  const defaults =
    config.modality === 'fullpage'
      ? DEFAULT_SIZE.fullpage
      : DEFAULT_SIZE.floating;
  const w = config.window?.width ?? defaults.width;
  const h = config.window?.height ?? defaults.height;

  return (
    `width=${w},height=${h},` +
    'scrollbars=no,menubar=no,toolbar=no,location=no,status=no'
  );
}

/**
 * Build the popup's bootstrap HTML. It imports the runtime in the popup realm
 * (defining `ps-pype`), then posts a nonce-tagged handshake back to the opener.
 * The Loader — not this document — creates and drives the `<ps-pype>` element.
 */
function bootstrapDoc(opts: {
  runtimeUrl: string;
  nonce: string;
  title: string;
  theme: string | null;
  openerOrigin: string;
  lang: string;
}): string {
  const { runtimeUrl, nonce, title, theme, openerOrigin, lang } = opts;
  const themeAttr = theme ? ` data-theme="${escapeHtml(theme)}"` : '';
  // `lang` drives screen-reader pronunciation; reflect the configured language
  // rather than hardcoding English.
  return (
    `<!DOCTYPE html><html lang="${escapeHtml(lang)}"><head><meta charset="utf-8">` +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    `<title>${escapeHtml(title)}</title>` +
    '<style>html,body{margin:0;padding:0;height:100%;width:100%;overflow:hidden;}' +
    'ps-pype{display:block;width:100%;height:100%;}</style>' +
    `</head><body${themeAttr}>` +
    '<script type="module">' +
    `const N=${jsString(nonce)};` +
    `const U=${jsString(runtimeUrl)};` +
    // Post the handshake back to the opener's origin, not "*" — the popup is
    // same-origin, so we know exactly who should receive it.
    `const O=${jsString(openerOrigin)};` +
    'const P=window.opener||window.parent;' +
    'import(U).then(function(){' +
    `P&&P.postMessage({source:${jsString(READY_MESSAGE)},nonce:N},O);` +
    '}).catch(function(e){' +
    `P&&P.postMessage({source:${jsString(ERROR_MESSAGE)},nonce:N,message:String((e&&e.message)||e)},O);` +
    '});' +
    '</' +
    'script></body></html>'
  );
}

/**
 * Open the popup render target and write its bootstrap document.
 *
 * MUST be called synchronously inside the user gesture that triggered
 * `boot()`, or the browser's popup blocker rejects `window.open` (→ returns
 * `null`, which the Loader turns into a `WINDOW_BLOCKED` error).
 *
 * @returns A handle, or `null` if the popup was blocked / un-writable.
 * @throws {PypeLoaderError} `WINDOW_BLOCKED` when the runtime URL can't resolve.
 */
export function openPypeWindow(config: AppConfig): OpenWindowResult | null {
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return null;
  }

  // Resolve BEFORE opening so a misconfiguration doesn't leave a blank popup.
  const runtimeUrl = resolveWindowRuntimeUrl(config);
  const name = config.window?.name ?? `pypestream-${config.appId ?? 'pype'}`;

  const popup = window.open('', name, buildFeatures(config));
  if (!popup) return null;

  // COOP fail-fast: a host page served with `Cross-Origin-Opener-Policy:
  // same-origin` severs the popup↔opener link. Then the popup can't hand its
  // runtime back to us and every getter that reads the live element is blocked,
  // so `boot()` would otherwise hang. Detect the severed link now and fail
  // loudly. Guarded because cross-context property access can itself throw.
  let openerLinked = false;
  try {
    openerLinked = popup.opener === window;
  } catch {
    openerLinked = false;
  }
  if (!openerLinked) {
    safe(() => popup.close());
    throw new PypeLoaderError(
      LoaderErrorCode.WINDOW_BLOCKED,
      "target: 'window' cannot run: the popup's opener link is unavailable. " +
        'This usually means the embedding page sets `Cross-Origin-Opener-' +
        'Policy: same-origin`, which severs the popup from its opener. Relax ' +
        "COOP on the host page, or use `target: 'inline'`."
    );
  }

  const nonce = `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
  const title = config.customerName ? `${config.customerName} — Chat` : 'Chat';

  // Built once and reused verbatim by reinject() on reload — reusing the same
  // string guarantees the same nonce, or the re-handshake would be ignored.
  const bootstrapHtml = bootstrapDoc({
    runtimeUrl,
    nonce,
    title,
    theme: config.theme,
    openerOrigin: window.location.origin,
    lang: config.language,
  });
  const writeBootstrap = (): void => {
    popup.document.open();
    popup.document.write(bootstrapHtml);
    popup.document.close();
    // Mark the import as in flight so a concurrent second open adopts rather
    // than clobbers (cleared when the handshake settles, in `onMessage`).
    setBootstrapping(popup, true);
  };

  // Adopt an already-running popup instead of clobbering it: `window.open('',
  // name)` returns the existing same-name window WITHOUT navigating, so if it's
  // already running a chat — OR still mid-bootstrap from a just-prior open —
  // skip the bootstrap write and reuse it.
  const adopted = runtimeAlreadyRunning(popup) || runtimeBootstrapping(popup);

  if (!adopted) {
    try {
      writeBootstrap();
    } catch {
      safe(() => popup.close());
      return null;
    }
  }

  const closeCbs: Array<() => void> = [];
  const reloadCbs: Array<() => void> = [];
  let resolvedReady = false;
  let closed = false;
  let pollId: ReturnType<typeof setInterval> | undefined;
  let readyResolve!: () => void;
  let readyReject!: (reason: unknown) => void;
  const ready = new Promise<void>((resolve, reject) => {
    readyResolve = resolve;
    readyReject = reject;
  });
  // A close/teardown before the handshake must settle `ready` so the awaiting
  // `boot()` rejects instead of hanging. This internal catch marks the promise
  // handled, so settling it has no `unhandledrejection` side effect on the host
  // when the consumer isn't separately awaiting it.
  ready.catch(() => {});

  const rejectReadyIfPending = (reason: string): void => {
    if (resolvedReady) return;
    resolvedReady = true;
    readyReject(new Error(reason));
  };

  // Deferred for the in-flight `reinject()` cycle (reload restore). Unlike the
  // one-shot `ready` latch, this re-arms on every `reinject()` so each reload's
  // handshake settles the promise the Loader is awaiting. Null when no reinject
  // is pending. A timeout backstops a stalled re-`import()` that never posts.
  let reinjectResolve: (() => void) | null = null;
  let reinjectReject: ((reason: unknown) => void) | null = null;
  let reinjectTimer: ReturnType<typeof setTimeout> | undefined;

  const clearReinject = (): void => {
    reinjectResolve = null;
    reinjectReject = null;
    if (reinjectTimer !== undefined) {
      clearTimeout(reinjectTimer);
      reinjectTimer = undefined;
    }
  };

  /** Settle any pending reinject cycle; no-op when none is armed. */
  const settleReinject = (
    outcome: { ok: true } | { ok: false; reason: unknown }
  ): void => {
    const resolve = reinjectResolve;
    const reject = reinjectReject;
    clearReinject();
    if (outcome.ok) resolve?.();
    else reject?.(outcome.reason);
  };

  // Adopting: the runtime is already present, so there's no handshake to await —
  // settle `ready` now and bring the existing window forward.
  if (adopted) {
    resolvedReady = true;
    readyResolve();
    safe(() => popup.focus());
  }

  const onMessage = (ev: MessageEvent): void => {
    // Only trust the popup we opened, from our own origin — the nonce already
    // correlates the handshake, and these close the gap where any other frame
    // could post a spoofed ready/error to the opener window. Synthetic test
    // events carry neither field, so both checks are skip-if-absent.
    if (ev.source && ev.source !== popup) return;
    if (ev.origin && ev.origin !== window.location.origin) return;
    const data = ev.data as {
      source?: string;
      nonce?: string;
      message?: string;
    } | null;
    if (!data || data.nonce !== nonce) return;
    // Either outcome ends the in-flight import, so clear the bootstrapping mark
    // — a later open must not adopt a settled (esp. FAILED) popup and then hang
    // on `whenDefined('ps-pype')`.
    if (data.source === READY_MESSAGE || data.source === ERROR_MESSAGE) {
      setBootstrapping(popup, false);
    }
    if (data.source === READY_MESSAGE) {
      if (!resolvedReady) {
        resolvedReady = true;
        readyResolve();
        return;
      }
      // A later READY is the re-injected runtime after a reload — settle the
      // active `reinject()` promise so the Loader's remount proceeds. (No-op if
      // no reinject is pending, e.g. a stray duplicate handshake.)
      settleReinject({ ok: true });
    } else if (data.source === ERROR_MESSAGE) {
      if (!resolvedReady) {
        resolvedReady = true;
        readyReject(
          new Error(data.message || 'Pype runtime import failed in the popup.')
        );
        return;
      }
      // A later ERROR is a FAILED re-import after a reload. Previously this was
      // dropped (`ready` already resolved) and the remount hung on
      // `whenDefined('ps-pype')` forever; now it rejects the reinject promise so
      // the Loader can fall back to closing the popup instead of leaving it blank.
      settleReinject({
        ok: false,
        reason: new Error(
          data.message || 'Pype runtime re-import failed in the popup.'
        ),
      });
    }
  };
  window.addEventListener('message', onMessage);

  const teardownWatchers = (): void => {
    window.removeEventListener('message', onMessage);
    if (pollId !== undefined) {
      clearInterval(pollId);
      pollId = undefined;
    }
  };

  const fireClosed = (): void => {
    if (closed) return;
    closed = true;
    teardownWatchers();
    // Settle `ready` first: if the popup died mid-import, the awaiting `boot()`
    // must reject, not hang. (No-op once the handshake already resolved it.)
    rejectReadyIfPending(
      'The popup was closed before the widget finished mounting.'
    );
    // A close mid-reinject must also reject the awaiting remount, not hang.
    settleReinject({
      ok: false,
      reason: new Error('The popup was closed before it finished reloading.'),
    });
    closeCbs.forEach((cb) => safe(cb));
  };

  // Armed by `notifyMounted()` once a `<ps-pype>` is live; gates reload
  // detection so a slow initial import isn't mistaken for a reload.
  let mounted = false;

  // `popup.closed` polling is the reliable cross-browser close signal; the same
  // loop also detects a user refresh. On reload the popup's realm resets, so the
  // runtime's custom element is no longer defined in it — `popup.closed` stays
  // false, so this element-gone probe is the only signal. Edge-triggered (clears
  // `mounted` before firing) so it can't storm during the async remount.
  pollId = setInterval(() => {
    if (popup.closed) {
      fireClosed();
      return;
    }
    if (!mounted) return;
    let runtimeGone = false;
    try {
      runtimeGone = !popup.customElements?.get('ps-pype');
    } catch {
      // Cross-context access can throw mid-teardown — not a reload signal.
      runtimeGone = false;
    }
    if (runtimeGone) {
      mounted = false;
      reloadCbs.forEach((cb) => safe(cb));
    }
  }, CLOSE_POLL_MS);

  return {
    window: popup,
    ready,
    adopted,
    onClose(cb) {
      closeCbs.push(cb);
    },
    onReload(cb) {
      reloadCbs.push(cb);
    },
    notifyMounted() {
      mounted = true;
    },
    reinject() {
      // Supersede any still-pending prior cycle so its awaiter doesn't dangle.
      settleReinject({
        ok: false,
        reason: new Error('Superseded by a newer reload.'),
      });
      const promise = new Promise<void>((resolve, reject) => {
        reinjectResolve = resolve;
        reinjectReject = reject;
        reinjectTimer = setTimeout(() => {
          settleReinject({
            ok: false,
            reason: new Error(
              'The popup did not re-import the runtime in time after reloading.'
            ),
          });
        }, REINJECT_TIMEOUT_MS);
      });
      // Mark handled so a teardown-driven rejection can't surface as an
      // `unhandledrejection` on the host when the Loader isn't awaiting it
      // (mirrors the `ready.catch(() => {})` guard above).
      promise.catch(() => {});
      safe(writeBootstrap);
      return promise;
    },
    focus() {
      safe(() => popup.focus());
    },
    close() {
      // Intentional teardown: stop watchers first so `onClose` does NOT fire.
      teardownWatchers();
      closed = true;
      // A teardown during the import window must also settle `ready`, or the
      // in-flight `boot()` awaiting it is orphaned.
      rejectReadyIfPending(
        'The popup was closed before the widget finished mounting.'
      );
      // Likewise settle a pending reinject so an awaiting remount rejects.
      settleReinject({
        ok: false,
        reason: new Error('The popup was closed during reload.'),
      });
      safe(() => popup.close());
    },
    get closed() {
      return closed || popup.closed;
    },
  };
}
